import { PropsWithChildren, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { defineMessages, IntlShape, useIntl } from "react-intl";
import {
  autoUpdate,
  offset,
  Placement,
  safePolygon,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions
} from "@floating-ui/react";
import { IconChevronDown, IconHeart, IconMenu2, IconShoppingCart, IconUserCircle, IconWorld, IconX } from "@tabler/icons-react";
import { classNames, useAppConfig } from "@ct-react/core";
import { LangLink, LocaleLink, useLocaleContext, useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { useVisitor } from "@ct-react/visitor";
import { ArticleType, buildSearchUrl, buildPageUrl } from "@shared/url";
import { allLocales } from "@shared/locale";
import { actions, menu_translations } from "../../i18n/sharable-defs";
import { AnyNavItem, Navigation } from "../../models/nav";
import { useMediaQueryDown } from "../../hooks/media-queries";
import { useElementSize } from "../../hooks/dom";
import { useCartCount } from "../../hooks/cart";
import { Wrapper } from "./wrapper";
import { useWishlist } from "../../contexts/wishlist";
import { useScrollY } from "../../hooks/scroll";

import Logo from "../../assets/componable-svgs/Logo-texte-black.svg?url";
import LogoWhite from "../../assets/componable-svgs/Logo-texte-white.svg?url";

import French from "../../assets/flags/french.jpg";
import German from "../../assets/flags/german.jpg";
import English from "../../assets/flags/english.jpg";

import "./header.scss";

const buildMenu = (intl: IntlShape, transLib: any): Navigation => ([
  {
    type: "sub-nav",
    label: intl.formatMessage(menu_translations.rental),
    items: [ ArticleType.RENTAL, ArticleType.SEASONAL, ArticleType.ANNUAL ].map(t => ({
      type: "link",
      label: transLib.match("articleTypes", t),
      target: buildSearchUrl(t)
    }))
  },
  {
    type: "link",
    label: transLib.match("articleTypes", ArticleType.SALE),
    target: buildSearchUrl(ArticleType.SALE)
  },
  {
    type: "sub-nav",
    label: intl.formatMessage(menu_translations.services),
    items : [
      {
        label: intl.formatMessage(menu_translations.management),
        slug: "gerance"
      },
      {
        label: intl.formatMessage(menu_translations.estimation),
        slug: "estimation-immobiliere"
      },
      {
        label: intl.formatMessage(menu_translations.renovation),
        slug: "renovation"
      }
    ].map(item => ({
      type: "link",
      label: item.label,
      target: item.slug
    })),
  },
  {
    type: "sub-nav",
    label: intl.formatMessage(menu_translations.agency),
    items: [
      {
        label: intl.formatMessage(menu_translations.about),
        slug: "a-propos",
      },
      {
        label: intl.formatMessage(menu_translations.nendaz),
        slug: "nendaz",
      },
      {
        label: intl.formatMessage(menu_translations.news),
        slug: "actualites",
      },
    ].map(item => ({
      type: "link",
      label: item.label,
      target: item.slug
    })),
  },
  {
    type: "link",
    label: intl.formatMessage(menu_translations.contact),
    target: "contact"
  }
]);

type SubProps = {
  className?: string;
  label: string | ReactNode;
  placement?: Placement;
}

const SubNav = ({ className, label, placement = "bottom", children }: PropsWithChildren<SubProps>) => {

  const [ open, setOpen ] = useState<boolean>(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement,
    whileElementsMounted: autoUpdate,
    middleware: [ offset(4) ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay: 75, handleClose: safePolygon() }),
    useFocus(context),
    useDismiss(context, { ancestorScroll: true })
  ]);

  const refClasses = classNames("with-subs", className, { "opened": open });

  return (
    <>
      <button ref={refs.setReference} {...getReferenceProps()}
              type="button" className={refClasses}>
        {label}
        <IconChevronDown className="c-icon" />
      </button>
      {open &&
        <div ref={refs.setFloating} {...getFloatingProps()}
             className="sub-content" style={{...floatingStyles}}>
          {children}
        </div>
      }
    </>);

}

const Header = () => {

  const intl = useIntl();
  const { options: { withLogin } } = useAppConfig();
  const { transLib } = useLocaleFormatter();
  const { countItem } = useWishlist();

  const { locale } = useLocaleContext();
  const location = useLocation();
  const navigate = useLocaleNavigate();
  const isHome = /^\/(fr|en|de)?\/?$/.test(location.pathname);

  const { isLoggedIn } = useVisitor();
  const cartCount = useCartCount();
  const { ref, size } = useElementSize();
  const isScrolled = useScrollY(150);
  const [langFlag, setLangFlag] = useState<any>(French);
  const [ mobileOpen, setMobileOpen ] = useState<boolean>(false);
  const menu = useMemo(() => buildMenu(intl, transLib), [ intl ]);

  const useIsSearch = () => {
    const { pathname } = useLocation();
    return /(location|vente)/.test(pathname);
  };

  useLayoutEffect(() => {
    if (size?.height) {
      document.documentElement.style.setProperty("--cgv-header-height", `${size.height}px`);
    }
  }, [ size?.height ]);

  useEffect(() => { // set language flag
    switch (locale.basename) {
      case "fr":
        setLangFlag(French);
        break;
      case "de":
        setLangFlag(German);
        break;
      case "en":
        setLangFlag(English);
        break;
      default:
        setLangFlag(French);
    }
  }, [locale]);

  const matchUrlSubNav = (items: AnyNavItem[]) => {
    return items.some(sub => location.pathname.includes(sub.target) );
  };

  const renderNavItem = useCallback((item: AnyNavItem, index: number, mobile: boolean = false) => {

    if (item.type === "link")
      return (
        <LocaleLink key={index} className={classNames("nav-item label",{active: location.pathname.includes(item.target) })}
                    to={item.target}
                    {...mobile && { onClick: () => setMobileOpen(false) }}>
          {item.label}
        </LocaleLink>);

    if (!mobile)
      return (
        <SubNav key={index} className={classNames("nav-item label",{active: matchUrlSubNav(item.items) })} placement="bottom-start"
                label={item.label}>
          <ul>{item.items.map((sub, jndex) => <li key={jndex}>{renderNavItem(sub, jndex)}</li>)}</ul>
        </SubNav>);

    return (
      <details key={index}>
        <summary className="nav-item label with-subs">
          {item.label}
          <IconChevronDown className="c-icon" />
        </summary>
        <ul className="sub-content">
          {item.items.map((sub, jndex) => <li key={jndex}>{renderNavItem(sub, jndex, mobile)}</li>)}
        </ul>
      </details>
    )
  }, [location]);

  const redirectTarget = useCallback((logout: boolean = false) => {
    const path = logout && location.pathname.match(/\/me\//)
      ? `/${locale.basename}`
      : location.pathname + location.search + location.hash;
    return encodeURIComponent(path);
  }, [ location ]);

  const withBurger = useMediaQueryDown("tablet");
  const navClasses = classNames("main-nav", { "mobile-opened": mobileOpen });

  return (
    <header ref={ref}
    className={classNames("main-header", [{ "is-home": isHome },{"is-search" : useIsSearch()},{"is-scrolled" : isScrolled}])}
    role="banner">
      <div className="wrap centered">
        <div className="brand keep">
          <LocaleLink to={"/"} aria-label="Retour à l'accueil">
            <img
              src={isHome ? LogoWhite : Logo}
              width={432}
              height={119}
              alt=""
            />
          </LocaleLink>
        </div>

        <nav className={navClasses} role="navigation">

          {!withBurger && menu.map((item, index) => renderNavItem(item, index))}

          {withLogin &&
            <SubNav className="nav-item account iconized keep"
                    label={<IconUserCircle className={classNames({ connected: isLoggedIn })} />}>
              {isLoggedIn &&
                <ul>
                  <li>
                    <LocaleLink to="/me/profile" className="nav-item label">
                      {intl.formatMessage(menu_translations.profile)}
                    </LocaleLink>
                  </li>
                  <li>
                    <a className="nav-item label" href={`/logout?target=${redirectTarget(true)}`}>
                      {intl.formatMessage(actions.logout)}
                    </a>
                  </li>
                </ul>
              }
              {!isLoggedIn &&
                <ul>
                  <li>
                    <a className="nav-item label" href={`/signup?target=${redirectTarget()}`}>
                      {intl.formatMessage(actions.register)}
                    </a>
                  </li>
                  <li>
                    <a className="nav-item label" href={`/login?target=${redirectTarget()}`}>
                      {intl.formatMessage(actions.login)}
                    </a>
                  </li>
                </ul>
              }
            </SubNav>
          }

          {!withBurger &&
            <>
              <button type="button" className="nav-item cart iconized keep"
                      onClick={() => navigate("/checkout/")}
                      aria-label="Accéder au panier">
                <IconShoppingCart />
                {(cartCount > 0) && <span className="badge">{cartCount}</span>}
              </button>

              <button type="button" className="nav-item iconized keep wishlist-icon"
                      onClick={() => navigate("/wishlist/")}
                      aria-label="Accéder à la liste de favoris">
                <IconHeart />
                {!!countItem()&&
                <span>
                  {countItem()}
                </span>
                }
              </button>

              <SubNav className="nav-item lang label iconized keep"
                      label={ <>
                        <img src={langFlag} alt={locale.label} width={16} height={12} />
                        <span>{locale.basename}</span>
                      </>}
                      placement="bottom-start">
                <ul className="chipped">
                  {allLocales.map((l, index) => (
                    <li key={index}
                        className={classNames("nav-item", "label", { active: locale.code === l.code })}>
                      <LangLink locale={l}>{l.label}</LangLink>
                    </li>)
                  )}
                </ul>
              </SubNav>
            </>
          }

          {withBurger &&
            <>
              <button type="button" className="nav-item burger iconized keep"
                      onClick={() => setMobileOpen(prev => !prev)}
                      aria-label="Menu" aria-expanded={mobileOpen}>
                {mobileOpen ? <IconX /> : <IconMenu2 />}
              </button>
              {mobileOpen &&
                <div className="nav-overlay">
                  {menu.map((item, index) => renderNavItem(item, index, true))}
                  <div>
                    <button type="button" className="nav-item cart iconized keep"
                            onClick={() => navigate("/checkout/")}
                            aria-label="Accéder au panier">
                        <IconShoppingCart />
                        {(cartCount > 0) && <span className="badge">{cartCount}</span>}
                    </button>

                    <button type="button" className="nav-item iconized keep wishlist-icon"
                            onClick={() => navigate("/wishlist/")}
                            aria-label="Accéder à la liste de favoris">
                      <IconHeart />
                      {!!countItem()&&
                      <span>
                        {countItem()}
                      </span>
                      }
                    </button>

                    <SubNav className="nav-item lang label iconized keep"
                            label={ <>
                              <img src={langFlag} alt={locale.label} />
                              <span>{locale.basename}</span>
                            </>}
                            placement="bottom-start">
                      <ul className="chipped">
                        {allLocales.map((l, index) => (
                          <li key={index}
                              className={classNames("nav-item", "label", { active: locale.code === l.code })}>
                            <LangLink locale={l}>{l.label}</LangLink>
                          </li>)
                        )}
                      </ul>
                    </SubNav>
                  </div>
                </div>
              }
            </>
          }

        </nav>
      </div>

    </header>);
}

export default Header;
